/*
Interpretes Sanity RichText

The various components and marks allow specific Sanity types and marks to be transformed into custom React components for customizable
and extendable RichText content rendering.
*/

import InstagramPost from './InstagramPost';
import Quote from './Quote';
import { contentTypeToPath } from '@/utils/api-helpers';
import { CONTENT_TYPE_PAGE } from '@/shared/constants';
import HIHLink from './Link';
import HIHImageCaption from './HIHImageCaption';
import { NewTabHint } from './NewTabHint';
import styles from './RichText.module.scss';

const RichTextComponents: any = {
  // The types are used to render specific heuteinhamburg sanity components
  types: {
    hihImage: ({ value }: { value: any }) => {
      return (
        <HIHImageCaption
          className={styles.hihImage}
          imageClassName={styles.hihImageImage}
          image={value}
          width={1600}
          height={1200}
        />
      );
    },
    hihInstagramPost: ({ value }: { value: { url: string } }) => {
      return (
        <InstagramPost
          className={['hihInstagramPost', styles.instagramPost].join(' ')}
          urlString={value.url}
        />
      );
    },
    hihQuote: ({
      value,
    }: {
      value: { text: any; title: string; disclaimer: string };
    }) => {
      return (
        <Quote
          className={styles.hihQuote}
          text={value.text}
          title={value.title}
          disclaimer={value.disclaimer}
        />
      );
    },
  },
  // Marks are used to render links. We differentiate between internal and external links
  marks: {
    linkInternal: ({ value, children }: { value: any; children: any }) => {
      const { type, slug } = value;
      // An internal link annotation without a target document (missing
      // reference) would otherwise produce a broken //null link -> render text only.
      if (!slug) return <>{children}</>;
      const href =
        type === CONTENT_TYPE_PAGE ? `/${slug}` : `/${contentTypeToPath(type)}/${slug}`;
      return (
        <HIHLink href={href} className={styles.link}>
          {children}
        </HIHLink>
      );
    },
    linkExternal: ({ value, children }: { value: any; children: any }) => {
      const { blank, href } = value;
      return (
        <HIHLink
          href={href}
          className={styles.link}
          target={blank ? '_blank' : undefined}
          rel={blank ? 'noopener noreferrer' : undefined}
        >
          {children}
          {blank && <NewTabHint />}
        </HIHLink>
      );
    },
  },
  // Block elements are used to render headings, paragraphs
  block: {
    h2: ({ children }: { children: string }) => <h2 className={styles.h2}>{children}</h2>,
    h3: ({ children }: { children: string }) => <h3 className={styles.h3}>{children}</h3>,
    h4: ({ children }: { children: string }) => <h4 className={styles.h4}>{children}</h4>,
    normal: ({ children }: { children: string }) => (
      <p className={styles.paragraph}>{children}</p>
    ),
  },
  list: {
    bullet: ({ children }: { children: any }) => (
      <ul className={styles.bulletList}>{children}</ul>
    ),
    number: ({ children }: { children: any }) => (
      <ol className={styles.numberList}>{children}</ol>
    ),
  },
};

export default RichTextComponents;
